import { Combobox, Group, Input, InputBase, Text, useCombobox } from '@mantine/core';
import React, { useMemo } from 'react';
import { JiraIssueTypeData } from '../../../../api/endpoints/jira';
import { Icon } from '../../../../concepts/resources/Icon';

interface JiraIssueTypeInputProps {
    label?: string;
    allowedValues: JiraIssueTypeData[];
    value: JiraIssueTypeData | null;
    onChange: (value: JiraIssueTypeData | null) => void;
    onSubmit?: (value: JiraIssueTypeData | null) => void;
}

export const JiraIssueTypeInput = React.memo(({ label, allowedValues, value, onChange, onSubmit }: JiraIssueTypeInputProps) => {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    label = label ?? 'Issue Type';

    // Transform Jira issue types to options format
    const options = useMemo(() =>
        (allowedValues ?? []).map((issueType: JiraIssueTypeData) => (
            <Combobox.Option value={issueType.id} key={issueType.id}>
                <Group gap="sm">
                    <Icon size="1.25" src={issueType.iconUrl} />
                    <Text>{issueType.name}</Text>
                </Group>
            </Combobox.Option>
        ))
        , [allowedValues]);

    // Find the issue type object that corresponds to the selected value
    const findIssueTypeById = (id: string | null): JiraIssueTypeData | null =>
        id && allowedValues?.find(type => type.id === id) || null;

    // Handle selection
    const onSelect = (selectedId: string) => {
        const next = findIssueTypeById(selectedId);
        onChange(next);
        onSubmit?.(next);
        combobox.closeDropdown();
    };

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={onSelect}
        >
            <Combobox.Target>
                <InputBase
                    component="button"
                    type="button"
                    pointer
                    rightSection={<Combobox.Chevron />}
                    rightSectionPointerEvents="none"
                    onClick={() => combobox.toggleDropdown()}
                    label={label}
                    required
                >
                    {value ? (
                        <Group gap="sm">
                            <Icon size="1.25" src={value.iconUrl} />
                            <Text>{value.name}</Text>
                        </Group>
                    ) : (
                        <Input.Placeholder>{`Select ${label.toLowerCase()}`}</Input.Placeholder>
                    )}
                </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options>{options}</Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
});
